import type {
  CustomDashboardInterface,
  DashboardLayout,
  DashboardTab,
  WidgetConfig,
} from '@interfaces/customDashboard'

// Sheets inside one custom dashboard (AB#6448). A dashboard with no `tabs` is
// one implicit sheet: top-level `layouts` and every widget. With tabs, each tab
// owns its grid and names its widgets; `widgets` still holds every widget.

/** URL search param holding the open sheet. Not `tab`: the drawer and the
 * single-signal chart already read that one. */
export const SHEET_PARAM = 'sheet'

export const newTabId = (): string =>
  `tab-${Date.now()}-${Math.random().toString(36).substring(2, 9)}`

/** Index of the open tab, or -1 for a dashboard without tabs. An unknown id
 * (a stale URL, a tab removed by undo) falls back to the first tab. */
export const activeTabIndex = (
  dashboard: Pick<CustomDashboardInterface, 'tabs'>,
  activeTabId: string | null,
): number => {
  const tabs = dashboard.tabs ?? []
  if (tabs.length === 0) return -1
  const index = tabs.findIndex((tab) => tab.id === activeTabId)
  return index === -1 ? 0 : index
}

/** The grid of the open tab (the whole dashboard's grid without tabs). */
export const activeLayouts = (
  dashboard: Pick<CustomDashboardInterface, 'tabs' | 'layouts'>,
  activeTabId: string | null,
): CustomDashboardInterface['layouts'] => {
  const tabs = dashboard.tabs ?? []
  const index = activeTabIndex(dashboard, activeTabId)
  const layouts = index === -1 ? dashboard.layouts : tabs[index].layouts
  return layouts?.lg ? layouts : {...layouts, lg: []}
}

/** Only the open tab's widgets, so hidden tabs do not subscribe or poll. */
export const activeWidgets = (
  dashboard: Pick<CustomDashboardInterface, 'tabs' | 'widgets'>,
  activeTabId: string | null,
): Record<string, WidgetConfig> => {
  const tabs = dashboard.tabs ?? []
  const index = activeTabIndex(dashboard, activeTabId)
  const widgets = dashboard.widgets ?? {}
  if (index === -1) return widgets
  return Object.fromEntries(
    tabs[index].widgetIds.filter((id) => id in widgets).map((id) => [id, widgets[id]]),
  )
}

/** What is stored: with tabs, top-level `layouts` mirrors the first tab, so a
 * reader that does not know tabs still sees a valid first sheet. */
export const dashboardContent = (
  dashboard: Pick<CustomDashboardInterface, 'tabs' | 'layouts' | 'widgets'>,
): Pick<CustomDashboardInterface, 'layouts' | 'widgets'> & {
  tabs: NonNullable<CustomDashboardInterface['tabs']>
} => {
  const tabs = dashboard.tabs ?? []
  return {
    layouts: tabs.length > 0 ? tabs[0].layouts : dashboard.layouts,
    widgets: dashboard.widgets,
    tabs,
  }
}

export const newWidgetId = (): string =>
  `widget-${Date.now()}-${Math.random().toString(36).substring(2, 9)}`

const sameName = (a: string, b: string): boolean =>
  a.trim().toLocaleLowerCase() === b.trim().toLocaleLowerCase()

/** "<name> (copy)", or "<name> (copy 2)", "(copy 3)"... when that one is taken
 * (AB#6648). `format` builds the translated name for a copy number. */
export const copyTabName = (
  name: string,
  existingNames: string[],
  format: (name: string, number: number) => string,
): string => {
  for (let number = 1; ; number += 1) {
    const candidate = format(name, number)
    if (!existingNames.some((existing) => sameName(existing, candidate))) return candidate
  }
}

/** A copy of one sheet, inserted right after it (AB#6648). Every widget is
 * deep-copied under a new id and the grid cells are re-keyed to match, so the
 * copy shares nothing with the original: widget state keyed by id (configs,
 * selections) stays separate. Pure: returns new `tabs` and `widgets`, or null
 * for an unknown sheet. */
export const duplicateTab = (
  dashboard: Pick<CustomDashboardInterface, 'tabs' | 'widgets'>,
  sourceTabId: string,
  copy: {id: string; name: string; newWidgetId?: () => string},
): {tabs: DashboardTab[]; widgets: Record<string, WidgetConfig>} | null => {
  const tabs = dashboard.tabs ?? []
  const index = tabs.findIndex((tab) => tab.id === sourceTabId)
  if (index === -1) return null
  const source = tabs[index]
  const widgets = dashboard.widgets ?? {}
  const makeId = copy.newWidgetId ?? newWidgetId

  const idMap = new Map<string, string>()
  const copiedWidgets: Record<string, WidgetConfig> = {}
  source.widgetIds.forEach((oldId) => {
    if (!(oldId in widgets) || idMap.has(oldId)) return
    const id = makeId()
    idMap.set(oldId, id)
    copiedWidgets[id] = {...(JSON.parse(JSON.stringify(widgets[oldId])) as WidgetConfig), id}
  })

  const copyCells = (cells?: DashboardLayout[]): DashboardLayout[] =>
    (cells ?? [])
      .filter((cell) => idMap.has(cell.i))
      .map((cell) => ({
        ...(JSON.parse(JSON.stringify(cell)) as DashboardLayout),
        i: idMap.get(cell.i)!,
      }))

  const layouts: DashboardTab['layouts'] = {lg: copyCells(source.layouts?.lg)}
  if (source.layouts?.md) layouts.md = copyCells(source.layouts.md)
  if (source.layouts?.sm) layouts.sm = copyCells(source.layouts.sm)

  const tab: DashboardTab = {id: copy.id, name: copy.name, layouts, widgetIds: [...idMap.values()]}
  return {
    tabs: [...tabs.slice(0, index + 1), tab, ...tabs.slice(index + 1)],
    widgets: {...widgets, ...copiedWidgets},
  }
}
